<template>
  <div v-if="userHasNoProducts" class="error-message">
    <div class="error-message__icon">
      <AppIcon name="warning" size="headline-m" />
    </div>
    <div class="error-message__content">
      <div class="error-message__content-title">
        {{ t('home.error_title_insufficient_permissions') }}
      </div>
      <div class="error-message__content-text">
        {{ t('home.error_body_insufficient_permissions') }}
        <u>
          {{ t('home.error_body_insufficient_permissions.admin') }}
        </u>
      </div>
    </div>
  </div>

  <NavHeader>
    <div class="row gap-2">
      <AppIcon
        name="settings"
        size="body-xl"
        class-name="color-lighter"
        :clickable="true"
        :onClick="onClickSettings"
      />
      <UserIcon />
    </div>
  </NavHeader>
  <header
    class="header"
    :class="{
      'disabled-text': userHasNoProducts
    }"
  >
    <h3>
      {{ t('home.welcome') }}
    </h3>
    <p v-if="authStore.authMethod !== AuthMethods.FranceMock">
      {{ t('home.description') }}
    </p>
  </header>
  <main class="tasks">
    <TaskCard
      v-for="(task, index) in tasks"
      :key="index"
      :icon="task.icon"
      :title="task.title"
      :description="task.description"
      :disabled="task.disabled"
      @click="handleTask(task.routeName)"
    />
    <FloatingCard v-if="!tasks || tasks.length === 0" class="no-tasks">
      <p>{{ t('home.no_tasks_message') }}</p>
    </FloatingCard>
  </main>
</template>

<script lang="ts" setup>
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import FloatingCard from '@/components/common/floating-card/FloatingCard.vue';
import NavHeader from '@/components/common/nav-header/NavHeader.vue';
import UserIcon from '@/components/Home/UserIcon.vue';
import RouteNames from '@/configuration/router/names.routes';
import { ErrorWithToastData } from '@/exceptions/ErrorWithToastData';
import { AuthMethods } from '@/models/auth-methods';
import { TaskRegistry } from '@/models/tasks.models';
import moduleAvailabilityService from '@/services/ModuleAvailabilityService';
import navigationService from '@/services/NavigationService';
import { useAuthStore } from '@/stores/authStore';
import { useToasterStore } from '@/stores/useToasterStore';
import { DocumentValidationUtils } from '@/utils/document-validation.utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TaskCard from '../components/common/task-card/TaskCard.vue';

const { t } = useI18n();
const toaster = useToasterStore();
const authStore = useAuthStore();

const tasks = computed(() => {
  // Get modules supported on the current platform
  const supportedModules = moduleAvailabilityService.getSupportedModules();

  return supportedModules.map((moduleKey) => {
    const task = TaskRegistry.tasks[moduleKey];
    const isAvailable = moduleKey in authStore.products.modules;

    return {
      ...task,
      disabled: !isAvailable
    };
  });
});

const userHasNoProducts = computed(() => {
  return !tasks.value.length || tasks.value.every((task) => task.disabled);
});

async function validateReviewRequirements() {
  await DocumentValidationUtils.validateReviewRequirements(t);
}

async function validateTaskRequirements(routeName: string) {
  if (!routeName) return;

  switch (routeName) {
    case RouteNames.Reviewing: {
      await validateReviewRequirements();
      break;
    }
    // TODO: restrict other cases for deployment needs
    default:
      break;
  }
}

async function handleTask(routeName: string | undefined) {
  try {
    if (routeName) {
      await validateTaskRequirements(routeName);
      navigationService.navigateTo({ name: routeName });
    } else {
      throw new Error('Task not implemented');
    }
  } catch (error) {
    const toast =
      error instanceof ErrorWithToastData
        ? { title: error.message, body: error.body }
        : {
            title: t('contextual_errors.unknown.title'),
            body: t('contextual_errors.unknown.body')
          };

    toaster.error(toast);
    console.error(error);
  }
}

function onClickSettings() {
  navigationService.navigateTo({ name: RouteNames.UserSettings });
}
</script>

<style scoped lang="scss">
.error-message {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: $space-2;
  gap: $space-2;
  color: $color-error-500;
  border: 1px solid $color-error-500;
  border-radius: $radius-3;
  margin-bottom: $space-3;
  text-align: left;

  &__icon {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &-title {
      font-size: $body-s-font-size;
      line-height: $body-s-line-height;
      font-weight: $fw-medium;
    }

    &-text {
      font-size: $body-xs-font-size;
      line-height: $body-xs-line-height;
      font-weight: $fw-regular;
    }
  }
}

.header {
  margin-bottom: $space-2;
  text-align: left;

  &.disabled-text {
    color: $color-text-disabled;
  }
}

.tasks {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.header-icons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $space-2;
}

.no-tasks {
  margin-top: $space-2;
  text-align: center;
}

// Styles for big screens
@media (min-height: $small-screen-height) {
  .header {
    margin-bottom: $space-4;
  }

  .tasks {
    gap: $space-1;
  }

  .header-icons {
    margin-bottom: $space-4;
  }

  .no-tasks {
    margin-top: $space-4;
  }

  .error-message__content-title {
    font-size: $body-m-font-size;
    line-height: $body-m-line-height;
  }

  .error-message__content-text {
    font-size: $body-s-font-size;
    line-height: $body-s-line-height;
  }
}
</style>
